import React, { useEffect, useState } from 'react';
import ServiceCard from '../../components/serviceCard/ServiceCard';
import { useAuth } from '../../context/DBContext';
import {
  Layout,
  Spin,
  Row,
  Col,
  Form,
  Input,
  Typography,
  Pagination,
  Modal,
  message,
} from 'antd';
import { LoadingOutlined, SearchOutlined } from '@ant-design/icons';

const { Content } = Layout;
const { Title, Text } = Typography;

const ServicePage = () => {
  const [services, setServices] = useState([]);
  const { fetchServices, deleteService, currentUser, getUserInfo } = useAuth();
  const [loading, setLoading] = useState(false);
  const [filteredServices, setFilteredServices] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10); // Number of items per page
  const [form] = Form.useForm();
  const [userInfo, setUserInfo] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const data = await fetchServices();
        setServices(data);
        setFilteredServices(data);
      } catch (error) {
        // console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [fetchServices]);

  useEffect(() => {
    if (currentUser) {
      getUserInfo(currentUser.uid).then((data) => {
        setUserInfo(data);
      }).catch((error) => {
        console.error('Failed to fetch user info');
      });
    }
  }, [currentUser, getUserInfo]);

  const handleSearch = (e) => {
    const searchTerm = e.target.value.toLowerCase();
    if (!searchTerm) {
      setFilteredServices(services);
      return;
    }

    const filtered = services.filter((service) =>
      Object.values(service).some(
        (value) =>
          value &&
          value.toString().toLowerCase().includes(searchTerm)
      )
    );

    setFilteredServices(filtered);
  };

  const handlePageChange = (page, pageSize) => {
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  const truncateText = (text, maxLength) => {
    if (!text) return '';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  const handleDeleteService = (serviceId, serviceName) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this service?',
      content: (
        <div>
          <p><strong>{serviceName}</strong></p>
          <p style={{ color: 'red' }}>Warning: This will permanently delete:</p>
          <ul style={{ color: 'red' }}>
            <li>All time slots and bookings</li>
            <li>All reviews</li>
            <li>Remove from all users' favorites</li>
            <li>All business claim records</li>
          </ul>
          <p>This action cannot be undone.</p>
        </div>
      ),
      okText: 'Yes, delete it',
      okType: 'danger',
      cancelText: 'No, keep it',
      onOk: async () => {
        setDeleteLoading(true);
        try {
          await deleteService(serviceId);
          // Remove the deleted service from both lists
          setServices(prevServices => prevServices.filter(s => s.id !== serviceId));
          setFilteredServices(prevFiltered => prevFiltered.filter(s => s.id !== serviceId));
          message.success('Service deleted successfully');
        } catch (error) {
          console.error('Error deleting service:', error);
          // message.error('Failed to delete service');
        } finally {
          setDeleteLoading(false);
        }
      }
    });
  };

  const paginatedServices = () => {
    const lastIndex = currentPage * pageSize;
    const firstIndex = lastIndex - pageSize;
    return filteredServices.slice(firstIndex, lastIndex).map(service => ({
      ...service,
      name: truncateText(service.name, 50),      // Truncate name if longer than 50 chars
      description: truncateText(service.description, 150)  // Truncate description if longer than 150 chars
    }));
  };

  return (
    <Layout style={{ padding: '24px' }}>
      <Content>
        {/* Header Section */}
        <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
          <Col>
            <Title level={3}>Service List</Title>
          </Col>
        </Row>

        {/* Search Field */}
        <Form form={form} layout="inline" style={{ marginBottom: '24px', width: '100%' }}>
          <Form.Item style={{ flex: 1 }}>
          <Input
  placeholder="Search by name, address, category..."
  prefix={<SearchOutlined />}
  allowClear
  onChange={handleSearch}
  style={{
    width: '100%',
    padding: '4px 11px',  // Adjust padding to fit the content nicely within the reduced height
    borderRadius: '4px',  // Optional: rounding the corners a bit
    borderColor: '#d9d9d9',  // Optional: adjusting the border color to be more subtle
    fontSize: '14px'  // Adjust the font size if necessary
  }}
/>

          </Form.Item>
        </Form>

        {/* Loading State */}
        {loading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '50vh',
            }}
          >
            <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
          </div>
        ) : (
          <>
            {/* Services List */}
            <Row gutter={[10, 10]}>
              {paginatedServices().length > 0 ? (
                paginatedServices().map((service) => (
                  <Col key={service.id} xs={24}>
                    <ServiceCard 
                      {...service} 
                      isAdmin={userInfo?.role === 'admin'}
                      onDelete={handleDeleteService}
                    />
                  </Col>
                ))
              ) : (
                <Col span={24} style={{ textAlign: 'center', marginTop: '50px' }}>
                  <Text type="secondary" style={{ fontSize: '18px' }}>
                    No services found.
                  </Text>
                </Col>
              )}
            </Row>
            {/* Pagination */}
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
          <Pagination
            current={currentPage}
            total={filteredServices.length}
            pageSize={pageSize}
            onChange={handlePageChange}
          />
        </div>
          </>
        )}
      </Content>
    </Layout>
  );
};

export default ServicePage;
